import { useCallback } from "react";

import {
  hideLoadingFrame,
  setIsAgentsSignResponsibility,
  setPolicyErrorState,
  showLoadingFrame,
} from "@covertree/new-business-redux";

import { useNavigate } from "react-router";
import { useAppDispatch } from "@redux/hooks";
import { navigateToPolicyStep, NewPolicyRoutes } from "@routing/paths";
import {
  UnderwritingDecision,
  UnderwritingStatus,
} from "@covertree/new-business-graphql";

import { Checkout } from "@covertree/new-business-checkout";
import { useAccessPattern } from "@hooks/useAccessPattern";
import { featureFlags } from "@utils/featureFlags";
import { useBinderDocContext } from "@providers/BinderDocProvider";
import { ApolloError, isApolloError } from "@apollo/client";

export function CheckoutStep() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isInternalAgent } = useAccessPattern();
  const { isBinderAvailable } = useBinderDocContext();

  const onError = useCallback(
    (error: Error) => {
      if (isApolloError(error)) {
        const apolloError = error as ApolloError;
        if (apolloError.networkError) {
          dispatch(setPolicyErrorState(apolloError.networkError));
        } else if (apolloError.graphQLErrors.length > 0) {
          dispatch(setPolicyErrorState(apolloError.graphQLErrors[0]));
        }
      } else {
        dispatch(setPolicyErrorState(error));
      }

      navigateToPolicyStep(
        navigate,
        NewPolicyRoutes.ERROR,
        NewPolicyRoutes.CHECKOUT,
        dispatch
      );
    },
    [dispatch, navigate]
  );

  const onUWDecision = useCallback(
    (underwritingDecision: UnderwritingStatus) => {
      switch (underwritingDecision.decision) {
        case UnderwritingDecision.Reject:
          return navigateToPolicyStep(
            navigate,
            NewPolicyRoutes.DECLINE_PROPERTY,
            NewPolicyRoutes.POLICY_SUMMARY,
            dispatch,
            { underwritingDecision },
            true
          );
        case UnderwritingDecision.None:
          return navigateToPolicyStep(
            navigate,
            NewPolicyRoutes.UNDERWRITING_REVIEW,
            NewPolicyRoutes.POLICY_SUMMARY,
            dispatch,
            { underwritingDecision },
            true
          );
      }
    },
    [dispatch, navigate]
  );

  const onLoadingStart = useCallback(() => {
    dispatch(
      showLoadingFrame({
        messages: [
          "Checking policy underwriting status",
          "Creating payment plans",
          "Dotting our i’s and crossing our t’s",
        ],
        maxLoadTime: 6,
      })
    );
  }, [dispatch]);

  const onLoadingEnd = useCallback(() => {
    dispatch(hideLoadingFrame());
  }, [dispatch]);

  const onPaymentSuccess = useCallback(
    (isAgentsSignResponsibility?: boolean) => {
      if (isAgentsSignResponsibility !== undefined) {
        dispatch(setIsAgentsSignResponsibility(isAgentsSignResponsibility));
      }
      return navigateToPolicyStep(
        navigate,
        NewPolicyRoutes.CONGRATULATIONS,
        NewPolicyRoutes.CHECKOUT
      );
    },
    [dispatch]
  );

  return (
    <Checkout
      isD2C={false}
      isBinderAvailable={isBinderAvailable}
      onError={onError}
      onLoadingStart={onLoadingStart}
      onLoadingEnd={onLoadingEnd}
      onPaymentSuccess={onPaymentSuccess}
      onUWDecision={onUWDecision}
      isInternalAgent={isInternalAgent}
      isAdditionalInterestsEnabled={featureFlags.policyDetailsAdditionalInterests.isEnabled()}
      monthlyPaymentPlanStates={featureFlags.monthlyPaymentPlanStates.getValue()}
    />
  );
}
